import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TeamsAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit3, ExternalLink, RefreshCw, Users } from "lucide-react";
import { getSport } from "@/lib/sports";

export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await TeamsAPI.list();
      setTeams(list);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const migrate = async () => {
    setMigrating(true);
    try {
      const res = await TeamsAPI.migrate();
      toast.success(
        `Migration : ${res.teams_created} équipes créées, ${res.players_linked} joueurs liés, ${res.standings_linked} classements liés`,
      );
      load();
    } catch {
      toast.error("Échec de la migration");
    } finally {
      setMigrating(false);
    }
  };

  const remove = async (team) => {
    if (!window.confirm(`Supprimer l'équipe « ${team.name} » ?`)) return;
    try {
      await TeamsAPI.remove(team.id);
      toast.success("Équipe supprimée");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-10 pb-16">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em] mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour Admin
        </Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
              Laser Tech Création · Espace Admin
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">
              Équipes
            </h1>
            <p className="text-zinc-400 font-body text-base mt-2">
              Plaque/médaillon connecté pour chaque équipe : roster, palmarès,
              historique des tournois, QR.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={migrate}
              disabled={migrating}
              variant="outline"
              data-testid="teams-migrate"
              className="border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 mr-2 ${migrating ? "animate-spin" : ""}`}
              />
              {migrating ? "Migration…" : "Importer depuis les clubs"}
            </Button>
            <Link to="/admin/teams/new" data-testid="teams-new">
              <Button
                className="text-black font-bold uppercase tracking-[0.18em] text-xs h-10 px-5"
                style={{
                  background:
                    "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
                }}
              >
                <Plus className="w-3.5 h-3.5 mr-2" /> Nouvelle équipe
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-zinc-500 py-16 font-body">
            Chargement…
          </div>
        ) : teams.length === 0 ? (
          <div className="metal-card rounded-xl p-10 text-center">
            <Users className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
            <p className="text-zinc-400 font-body mb-4">
              Aucune équipe pour le moment.
            </p>
            <Button
              onClick={migrate}
              disabled={migrating}
              variant="outline"
              className="border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-2" /> Générer depuis les
              clubs existants
            </Button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="teams-grid"
          >
            {teams.map((t) => {
              const sport = getSport(t.sport || "football");
              return (
                <div
                  key={t.id}
                  className="metal-card rounded-xl p-4 flex items-center gap-3 hover:border-white/30 transition-all"
                  data-testid={`team-card-${t.slug}`}
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 overflow-hidden border bg-white/5"
                    style={{
                      borderColor: `${t.primary_color || "#0066FF"}55`,
                    }}
                  >
                    {t.logo_url ? (
                      <img
                        src={t.logo_url}
                        alt={t.name}
                        className="w-full h-full object-contain p-1.5"
                      />
                    ) : (
                      <span
                        className="font-display font-black text-xl"
                        style={{ color: t.primary_color || "#0066FF" }}
                      >
                        {t.name?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight truncate">
                      {t.name}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate mt-0.5">
                      {[sport.label, t.category, t.season]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Link
                      to={`/team/${t.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`team-view-${t.slug}`}
                      className="w-7 h-7 rounded-md flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                    <Link
                      to={`/admin/teams/${t.id}`}
                      data-testid={`team-edit-${t.slug}`}
                      className="w-7 h-7 rounded-md flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white"
                    >
                      <Edit3 className="w-3 h-3" />
                    </Link>
                    <button
                      onClick={() => remove(t)}
                      data-testid={`team-delete-${t.slug}`}
                      className="w-7 h-7 rounded-md flex items-center justify-center bg-red-500/5 border border-red-500/30 text-red-400 hover:bg-red-500/15"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}
